import React, { useEffect } from 'react'
import { useRouter } from 'next/router'
import Head from 'next/head'
import Link from 'next/link'
import { AppProps } from 'next/app'
import * as Sentry from '@sentry/react'
import {
  Provider as SiteComponentsProvider,
  SearchContextProvider,
  HeaderContextProvider,
  ActiveStoryContextProvider,
} from '@izettle/public-site-components/dist/help-center'
import '@izettle/public-site-components/dist/global.css'
import '@izettle/public-site-components/dist/help-center.css'

import { storyblokInstance } from '@app/services/storyblok-service'
import { FavIcon } from '@app/components/favicon'
import { Seo } from '@app/components/seo'
import { Tracking } from '@app/components/tracking'
import { localeToCountryCode, localeToLangCode } from '../utils/i18n'
import { getHrefLangTags } from '../utils/hreflang-utils'

if (process.env.NEXT_PUBLIC_SENTRY_DSN) {
  Sentry.init({
    enabled: process.env.NODE_ENV === 'production',
    dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
  })
}

function Fallback() {
  return <h1>Something unexpected occurred</h1>
}

function MyApp({ Component, pageProps }: AppProps) {
  const { locale = '', linkMap, page, hrefLangAlternates } = pageProps
  const countryCode = localeToCountryCode(locale)
  const langCode = localeToLangCode(locale)

  let seoProps: any = null
  try {
    const { content, uuid } = page?.data?.story

    const hrefLangTags = getHrefLangTags({
      hreflangOverrides: content.hreflangOverrides,
      availableCountries: content.availableCountries,
      hrefLangAlternates,
    })

    seoProps = {
      ...content.seo,
      countryCode,
      availableCountries: content.availableCountries,
      urls: linkMap[uuid],
      hrefLangTags,
    }
  } catch {}

  useEffect(() => {
    document && (document.documentElement.lang = locale)
  }, [locale])

  return (
    <>
      <FavIcon />
      {seoProps && <Seo {...seoProps} />}
      <Tracking
        countryCode={countryCode}
        langCode={langCode}
        name={page?.data?.story?.name}
      />

      <Head>
        <script
          dangerouslySetInnerHTML={{
            __html: `var StoryblokCacheVersion = '${storyblokInstance.getCacheVersion()}';`,
          }}
        ></script>
      </Head>
      <SiteComponentsProvider
        provider="next"
        components={{ Link }}
        internalLinks={linkMap}
        lang={countryCode}
        hooks={{ useRouter }}
        storyUuid={page?.data?.story?.uuid}
      >
        <SearchContextProvider>
          <HeaderContextProvider>
            <ActiveStoryContextProvider>
              {React.createElement(Component, { ...pageProps })}
            </ActiveStoryContextProvider>
          </HeaderContextProvider>
        </SearchContextProvider>
      </SiteComponentsProvider>
    </>
  )
}

export default Sentry.withErrorBoundary(MyApp, { fallback: Fallback })
